import React, { useEffect, useRef } from "react";
import { Tabs, Tab } from "@nextui-org/react";
import { MentionItem } from "../types/mention.types";

interface MentionDropdownProps {
  isOpen: boolean;
  items: MentionItem[];
  highlightedId: string | null;
  isLoading: boolean;
  error: string | null;
  selectedTab: "all" | "basic" | "erc20";
  onSelect: (item: MentionItem) => void;
  onHighlight: (id: string | null) => void;
  onRetry: () => void;
  onTabChange: (tab: "all" | "basic" | "erc20") => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  className?: string;
}

const MentionDropdown: React.FC<MentionDropdownProps> = ({
  isOpen,
  items,
  highlightedId,
  isLoading,
  error,
  selectedTab,
  onSelect,
  onHighlight,
  onRetry,
  onTabChange,
  inputRef,
  className = "",
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (highlightedRef.current && isOpen) {
      highlightedRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedId, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, inputRef]);

  if (!isOpen) return null;

  return (
    <div className="absolute bottom-full left-0 w-full flex justify-center mb-0 z-50">
      <div
        ref={dropdownRef}
        className={`w-[80%] max-h-80 overflow-hidden bg-white/80 backdrop-blur-3xl border border-white/50 rounded-t-xl rounded-bl-none rounded-br-none shadow-[0_20px_60px_rgba(0,0,0,0.3),0_0_0_1px_rgba(255,255,255,0.1)] ${className}`}
      >
        <div className="p-2 border-b border-white/30 w-full">
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={(key) =>
              onTabChange(key as "all" | "basic" | "erc20")
            }
            size="sm"
            fullWidth={true}
            classNames={{
              tabList: "bg-white/20",
              tab: "text-gray-700 data-[selected=true]:text-gray-900",
              cursor: "bg-white/40",
            }}
          >
            <Tab key="all" title="All" />
            <Tab key="basic" title="Basic" />
            <Tab key="erc20" title="ERC20" />
          </Tabs>
        </div>

        <div className="max-h-60 overflow-y-auto">
          {isLoading && (
            <div className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-4 h-4 border-2 border-white/40 border-t-gray-600 rounded-full animate-spin"></div>
                <span className="text-sm text-gray-600">Searching...</span>
              </div>

              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 px-3 py-2 animate-pulse"
                >
                  <div className="w-7 h-7 bg-white/30 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-white/30 rounded w-20 mb-1"></div>
                    <div className="h-3 bg-white/30 rounded w-16"></div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && !isLoading && (
            <div className="p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm text-red-600">
                  Network error — retry
                </div>
                <button
                  onClick={onRetry}
                  className="px-2 py-1 text-xs bg-white/30 hover:bg-white/40 rounded transition-colors text-gray-700"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && (
            <>
              {items.length > 0 ? (
                <div className="py-1">
                  {items.map((item) => (
                    <div
                      key={`${item.source}:${item.id}`}
                      ref={highlightedId === item.id ? highlightedRef : null}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-all duration-200 relative ${
                        highlightedId === item.id
                          ? "bg-blue-100/60 shadow-sm before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-blue-500"
                          : "hover:bg-blue-50/80 hover:shadow-sm hover:before:absolute hover:before:left-0 hover:before:top-0 hover:before:bottom-0 hover:before:w-1 hover:before:bg-blue-500"
                      }`}
                      onClick={() => onSelect(item)}
                      onMouseEnter={() => onHighlight(item.id)}
                    >
                      <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0">
                        {item.icon ? (
                          <img
                            src={item.icon}
                            alt={item.label}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = `https://via.placeholder.com/28x28/666666/ffffff?text=${item.label}`;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-white/40 flex items-center justify-center text-xs font-medium text-gray-700">
                            {item.label.charAt(0)}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 truncate">
                            {item.label}
                          </span>
                          <span className="text-xs text-gray-500 truncate">
                            {item.networkCode}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span className="truncate">{item.name}</span>
                          {item.source === "erc20" && (
                            <span className="font-mono text-gray-300">
                              {item.id.slice(0, 6)}…{item.id.slice(-4)}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-shrink-0">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded font-medium shadow-sm ${
                            item.source === "basic"
                              ? "bg-blue-100/90 text-blue-700 border border-blue-200/50"
                              : "bg-emerald-100/90 text-emerald-700 border border-emerald-200/50"
                          }`}
                        >
                          {item.source === "basic" ? "BASIC" : "ERC20"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 text-center">
                  <div className="text-gray-600 text-sm mb-1">
                    No items found
                  </div>
                  <div className="text-gray-400 text-xs">Type to filter...</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MentionDropdown;
